import _ from 'lodash';
import { Bindable, Persistable, remote, Remoteable, Supertype, supertypeClass } from '@haventech/amorphic';
import { property } from '@haventech/supertype';
import { FetchResult } from 'apollo-fetch';
import requestPromise from 'request-promise-native';
import jwt from 'jsonwebtoken';
import { HtmlPdfUtils } from './HtmlPdfUtils';
import { HtmlPdfCoverPage } from './components/coverpage/HtmlPdfCoverPage';
import { HtmlPdfFooter } from './components/footer/HtmlPdfFooter';
import { HtmlPdfHeader } from './components/header/HtmlPdfHeader';
import { ProductApiTypes } from '@hit/policy-admin-types';
import {
  AdditionalData,
  IllustrationRider,
  IllustrationType,
  PaymentOption,
  ProductType,
  WholeLifePaymentTerms,
} from './HtmlPdfTypes';
import { IllustrationSummary } from './basicIllustration/templates/illustrationtable/IllustrationSummary';
import { IllustrationBasedOnDiv } from './basicIllustration/templates/illustrationtable/IllustrationBasedOnDiv';
import { BasicIllustrationStyles } from './basicIllustration/templates/common/Styles';
import { constants } from './basicIllustration/templates/constantsForPdf';
import { Header } from './basicIllustration/templates/common/Header';
import { Footer } from './basicIllustration/templates/common/Footer';
import { Utils } from '../../../common/js/Utils';
import { Product, RiderType } from '../../../common/js/static/Product';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { RateClasses } from '../../../common/js/static/RateClasses';
import { IllustrationRequest } from './IllustrationRequest';
import { IllustrationService } from './IllustrationService';
import { CdmService } from '../../../common/js/services/CdmService';
import { HtmlPdfNarrativeSummary } from './components/narrativesummary/NarrativeSummary';
import { HtmlPdfSummaryTable } from './components/summarytable/SummaryTable';
import { LoanType } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { HtmlPdfProductWarning } from './components/productwarning/HtmlPdfProductWarning';
import {
  ifiWholeLifeDividendColumnHeaderMap,
  ifiWholeLifeDividendWithRtrColumnHeaderMap,
  ifiWholeLifeSupplementalDividendColumnHeaderMap,
} from './IfiConstants';
import { ProductServiceClient, ProductSpecResponse } from '../../../common/js/productService/ProductServiceClient';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../common/js/logger/LoggerSingleton';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../common/js/logger/UILoggerSingleton';

class CssStyle {
  Styles: string = '';
}
type PageInfo = {
  title?: string;
  disclaimer?: string;
  footer?: string;
  offset: number;
};

export type Body = {
  html: string;
  formOrder?: string;
  formType?: string;
  options?: any;
  footer?: string;
};

@supertypeClass({ toClient: true, toServer: true })
export class HtmlPdfContentController extends Bindable(Remoteable(Persistable(Supertype))) {
  protected basicIllustrationData;
  protected basicIllustrationAdditionalData: AdditionalData = {
    paymentOptions: null,
    faceDecreasePaymentOptions: null,
    productBlock: null,
    productSpec: null,
  };
  protected supplementalIllustrationData;
  protected supplementalIllustrationAdditionalData: AdditionalData = {
    paymentOptions: null,
    faceDecreasePaymentOptions: null,
    productBlock: null,
  };
  protected tableColumns: string[] = [];
  protected supplementalTableColumns: string[] = [];
  @property({ toClient: false, getType: () => HtmlPdfCoverPage })
  private htmlPdfCoverPage: HtmlPdfCoverPage;

  @property({ toClient: false, getType: () => HtmlPdfFooter })
  private htmlPdfFooter: HtmlPdfFooter;

  @property({ toClient: false, getType: () => HtmlPdfHeader })
  private htmlPdfHeader: HtmlPdfHeader;

  @property({ getType: () => HtmlPdfNarrativeSummary })
  private narrativeSummary: HtmlPdfNarrativeSummary;

  @property({ getType: () => HtmlPdfProductWarning })
  private productWarning: HtmlPdfProductWarning;

  @property({ toClient: false, getType: () => HtmlPdfSummaryTable })
  private summaryTable: HtmlPdfSummaryTable;

  @property({ toClient: false, getType: () => IllustrationService })
  private illustrationService: IllustrationService;

  private readonly cpToMMDividends: Map<string, string> = new Map([
    ['Paid-up Additions', 'Paid-Up Additions'],
    ['Cash', 'Paid in Cash'],
    ['Reduce Premium/Paid-up Additions', 'Reduce Premium, excess to Paid-Up Additions'],
    ['Reduce Premium/Cash', 'Reduce Premium, excess to Cash'],
    ['Dividend Accumulations', 'Accumulate at Interest'],
  ]);
  private readonly dividendOptionMap = {
    PUA: 'Paid-up Additions',
    Cash: 'Cash',
    PRPUA: 'Reduce Premium/Paid-up Additions',
    PRCash: 'Reduce Premium/Cash',
    Accumulate: 'Dividend Accumulations',
  };

  static new() {
    return new HtmlPdfContentController().init();
  }

  init() {
    this.illustrationService = new IllustrationService();
    this.htmlPdfCoverPage = new HtmlPdfCoverPage();
    this.htmlPdfFooter = new HtmlPdfFooter();
    this.htmlPdfHeader = new HtmlPdfHeader();
    this.narrativeSummary = new HtmlPdfNarrativeSummary();
    this.productWarning = new HtmlPdfProductWarning();
    this.summaryTable = new HtmlPdfSummaryTable();

    return this;
  }

  @remote({ on: 'server' })
  async fetchInforceIllustrationDataFromProductService(
    illustrationRequest: IllustrationRequest,
    productSpec: ProductSpecResponse | null,
    inforceProductType: ProductType
  ): Promise<void | FetchResult[]> {
    this.basicIllustrationData = null;
    this.basicIllustrationAdditionalData = {
      paymentOptions: null,
      faceDecreasePaymentOptions: null,
      productBlock: null,
    };
    this.supplementalIllustrationData = null;
    this.supplementalIllustrationAdditionalData = {
      paymentOptions: null,
      faceDecreasePaymentOptions: null,
      productBlock: null,
    };
    this.tableColumns = [];
    this.supplementalTableColumns = [];
    return this.illustrationService
      .getInforceIllustrationData(illustrationRequest, inforceProductType)
      .then((response) => {
        if (inforceProductType === ProductType.Term) {
          const policy = response.data.termInforceIllustration.payload.policies[0];
          this.basicIllustrationData = {
            termInforceIllustration: {
              payload: {
                policies: [policy.illustrationTable],
              },
            },
          };
          this.basicIllustrationAdditionalData.paymentOptions = {
            monthly: {
              additionalCharge: 0,
              annualPremium: policy.modalTotalPremium.annual,
              annualTotal: policy.annualizedTotalPremium.monthly,
              annualWaiver: policy.modalWaiverPremium.annual,
              basePremium: policy.modalBasePremium.monthly,
              flatExtra: 0,
              riders: null,
              total: policy.modalTotalPremium.monthly,
              waiver: policy.modalWaiverPremium.monthly,
              premium: 0,
            },
            annual: {
              basePremium: policy.modalBasePremium.annual,
              flatExtra: 0,
              premium: policy.modalBasePremium.annual,
              total: policy.modalTotalPremium.annual,
              waiver: policy.modalWaiverPremium.annual,
              riders: null,
            },
            semiannual: {
              basePremium: policy.modalBasePremium.semiAnnual,
              premium: 0,
              flatExtra: 0,
              waiver: policy.modalWaiverPremium.semiAnnual,
              total: policy.modalTotalPremium.semiAnnual,
              annualTotal: policy.annualizedTotalPremium.semiAnnual,
              riders: null,
            },
            quarterly: {
              basePremium: policy.modalBasePremium.quarterly,
              premium: 0,
              flatExtra: 0,
              riders: null,
              waiver: policy.modalWaiverPremium.quarterly,
              total: policy.modalTotalPremium.quarterly,
              annualTotal: policy.annualizedTotalPremium.quarterly,
            },
          };
          this.basicIllustrationAdditionalData.productBlock = policy.productBlock;
          return Promise.resolve(response);
        } else if (inforceProductType === ProductType.WholeLife) {
          // @TODO investigate if this is actually used for whole life as there is a new whole life payment option that's created
          // downstream and it looks like this may not actually be necessary for that code since it's wrong anyway
          const policy = response[0].data.wholeLifeInforceIllustration.payload.policy;
          const hasRTR = illustrationRequest.riders.some((rider) => rider.type === RiderType.RTR);
          this.tableColumns = hasRTR
            ? ifiWholeLifeDividendWithRtrColumnHeaderMap[illustrationRequest.dividendOption]
            : ifiWholeLifeDividendColumnHeaderMap[illustrationRequest.dividendOption];
          this.basicIllustrationData = {
            wholeLifeInforceIllustration: {
              payload: {
                policy: policy,
              },
            },
          };
          this.basicIllustrationAdditionalData.paymentOptions = this.parsePaymentOptions(policy);
          this.basicIllustrationAdditionalData.productSpec = productSpec;

          // If there is a face amount change
          if (illustrationRequest.newFaceAmount > 0) {
            const supplementalPolicy = response[1].data.wholeLifeInforceIllustration.payload.policy;
            this.determineTableColumns(supplementalPolicy.illustrationList, illustrationRequest.dividendOption);
            this.supplementalIllustrationData = {
              wholeLifeInforceIllustration: {
                payload: {
                  policy: supplementalPolicy,
                },
              },
            };

            this.supplementalIllustrationAdditionalData.paymentOptions = this.parsePaymentOptions(supplementalPolicy);
          }

          return Promise.resolve(response);
        }
      });
  }

  // everything besides total is not used. Refer the getBody function for the real paymentOptions
  parsePaymentOptions(policy) {
    return {
      monthly: {
        additionalCharge: 0,
        annualPremium: policy.annualTotalPremium,
        annualTotal: policy.annualTotalPremium,
        annualWaiver: policy.annualWaiverPremium,
        basePremium: policy.monthlyBasePremium,
        flatExtra: 0,
        riders: null,
        total: policy.currentPremiums[1].totalInitialModalPremium, // takes the monthly premium applies to our current way of displaying
        waiver: policy.monthlyWaiverPremium,
        premium: 0,
      },
      annual: {
        basePremium: policy.annualBasePremium,
        flatExtra: 0,
        premium: policy.annualBasePremium,
        total: policy.currentPremiums[0].totalInitialModalPremiumAnnualized, // takes the yearly premium applies to our current way of displaying
        waiver: policy.annualWaiverPremium,
        riders: null,
      },
      semiannual: {
        basePremium: policy.semiAnnualBasePremium,
        premium: 0,
        flatExtra: 0,
        waiver: policy.semiAnnualWaiverPremium,
        total: policy.semiAnnualTotalPremium,
        annualTotal: policy.annualizedSemiAnnualTotalPremium,
        riders: null,
      },
      quarterly: {
        basePremium: policy.quarterlyBasePremium,
        premium: 0,
        flatExtra: 0,
        riders: null,
        waiver: policy.quarterlyWaiverPremium,
        total: policy.quarterlyTotalPremium,
        annualTotal: policy.annualizedQuarterlyTotalPremium,
      },
    };
  }

  // Take the default column list for this dividend option
  // Only add a column to the final list if there is at least one non-zero row
  // Currently this only applies to supplemental columns
  determineTableColumns(illustrationList, dividendOption: string) {
    const dividendOptionColumns = ifiWholeLifeSupplementalDividendColumnHeaderMap[dividendOption];
    for (const field of dividendOptionColumns) {
      for (const row of illustrationList) {
        if (row[field] !== 0) {
          this.supplementalTableColumns.push(field);
          break;
        }
      }
    }
  }

  buildWholeLifePaymentOptions(currentPremiums) {
    const wholeLifePaymentOptions = {};
    // loop thru the payment options and map them so they're easier to work with
    currentPremiums.forEach((element) => {
      wholeLifePaymentOptions[element.paymentFrequency.toLowerCase()] = {
        basePolicyPremium: element.basePolicyPremium,
        RTRPremium: element.RTRPremium,
        GIRPremium: element.GIRPremium,
        CIRPremium: element.CIRPremium,
        waiverPremium: element.waiverPremium,
        RTRWaiverPremium: element.RTRWaiverPremium,
        GIRWaiverPremium: element.GIRWaiverPremium,
        CIRWaiverPremium: element.CIRWaiverPremium,
        totalInitialModalPremiumAnnualized: element.totalInitialModalPremiumAnnualized,
        totalInitialModalPremium: element.totalInitialModalPremium,
      };
    });

    return wholeLifePaymentOptions;
  }

  @remote({ on: 'server' })
  async generateInforceIllustrationPDF(
    illustrationRequest: IllustrationRequest,
    productSpec: ProductSpecResponse | null,
    inforceProductType: ProductType
  ) {
    await this.fetchInforceIllustrationDataFromProductService(illustrationRequest, productSpec, inforceProductType);
    const headers = this.getHeaders();

    const body = await this.getBody(illustrationRequest, IllustrationType.Inforce, inforceProductType);

    const requestBody = {
      data: body,
      page: {
        height: '11in',
        width: '8.5in',
        border: '0.25in',
        header: {
          height: constants.HeaderHeight.Normal,
        },
        footer: {
          height: constants.FooterHeight.Normal,
        },
      },
    };

    LoggerSingleton.getInstance().info(
      {
        module: 'HtmlPdfContentController',
        function: 'generateInforceIllustrationPDF',
        context: {
          piiLevel: PiiLevelEnum.s2,
        },
        data: {
          illustrationRequest: IllustrationService.cleanIllustrationRequestForLogging(illustrationRequest),
        },
        category: Category.request,
      },
      'Html Content generation complete - sending to pdf stream now'
    );

    const options = {
      url: this.getHtmlToPdfStreamUrlForAgentChannel(),
      json: true,
      body: requestBody,
      method: 'POST',
      headers: headers,
    };

    return requestPromise(options);
  }

  /**
   *  This gets the body of the PDF and where most of the work happens calling other parts of the PDF
   * @param illustrationRequest:  string, @param illustrationType: enum - this could be used in the future to switch out different functions / components depending on the type right now everything is inforce
   */
  async getBody(
    illustrationRequest: IllustrationRequest,
    illustrationType: IllustrationType,
    inforceProductType: ProductType
  ): Promise<Body[]> {
    const lastPageFooter = await this.htmlPdfFooter.getFilledFooter(illustrationRequest, true);
    const allButLastPageFooter = await this.htmlPdfFooter.getFilledFooter(illustrationRequest, false);
    let wholeLifePaymentOptions: WholeLifePaymentTerms = {};
    let supplementalWholeLifePaymentOptions: WholeLifePaymentTerms = {};

    const hasSupplemental = inforceProductType === ProductType.WholeLife && illustrationRequest.newFaceAmount > 0;
    const pagesBeforeTabular =
      inforceProductType === ProductType.WholeLife
        ? constants.WholeInforcePagesBeforeTabular
        : constants.TermInforcePagesBeforeTabular;

    let productSelectionCode = illustrationRequest.productCode;
    if (
      illustrationRequest.productCode === Product.products.CASTermECP.code ||
      illustrationRequest.productCode === Product.products.CASTerm.code ||
      illustrationRequest.productCode === Product.products.MMTRADTerm.code ||
      illustrationRequest.productCode === Product.products.MMTRADTermECP.code
    ) {
      productSelectionCode = `${illustrationRequest.productCode}${illustrationRequest.term}`;
    } else if (
      illustrationRequest.productCode === Product.products.MMOPMTerm.code ||
      illustrationRequest.productCode === Product.products.MMOPMTermAPT80.code
    ) {
      productSelectionCode = this.basicIllustrationAdditionalData.productBlock;
    }

    let header;
    if (inforceProductType === ProductType.WholeLife) {
      header = await this.htmlPdfHeader.getHeaderBody(constants.ProductSelection[productSelectionCode]);

      wholeLifePaymentOptions = this.buildWholeLifePaymentOptions(
        this.basicIllustrationData.wholeLifeInforceIllustration.payload.policy.currentPremiums
      );

      if (hasSupplemental) {
        supplementalWholeLifePaymentOptions = this.buildWholeLifePaymentOptions(
          this.supplementalIllustrationData.wholeLifeInforceIllustration.payload.policy.currentPremiums
        );
      }
    } else if (inforceProductType === ProductType.Term) {
      header = await this.htmlPdfHeader.getTermInforceHeaderBody(constants.ProductSelection[productSelectionCode]);
    }

    const hasAlirRider = !!illustrationRequest.riders.find((rider) => rider.type === RiderType.ALIR);
    const coverpage: Body = {
      html: await this.htmlPdfCoverPage.getHtmlContent_coverPage(
        illustrationRequest,
        inforceProductType,
        this.basicIllustrationAdditionalData.productBlock
      ),
    };

    UILoggerSingleton.getInstance().info(
      {
        module: 'HtmlPdfContentController',
        function: 'generateInforceIllustrationPDF - getBody',
        context: {
          piiLevel: UIPiiLevelEnum.s2,
        },
        data: {
          illustrationRequest: illustrationRequest,
        },
        category: UICategory.request,
      },
      `generateInforceIllustrationPDF - Creating the body of the illustration`
    );
    let maindocument: Body[] = [
      {
        html: await this.narrativeSummary.getHtmlContent_NarrativeSummary(
          illustrationRequest,
          this.basicIllustrationAdditionalData,
          inforceProductType,
          wholeLifePaymentOptions
        ),
        footer: allButLastPageFooter,
      },
      {
        html: await this.narrativeSummary.getHtmlContent_NarrativeSummary_PageTwo(
          illustrationRequest,
          inforceProductType,
          this.basicIllustrationData.mecYear,
          hasSupplemental ? this.supplementalIllustrationData.mecYear : null,
          this.basicIllustrationAdditionalData.productBlock
        ),
        footer: allButLastPageFooter,
      },
      {
        html: await this.narrativeSummary.getHtmlContent_NarrativeSummary_PageThree(
          illustrationRequest,
          inforceProductType, // only whole life needs the issuedDate
          this.tableColumns,
          this.supplementalTableColumns,
          this.getLoanInterestAmountFromSpec(
            this.basicIllustrationAdditionalData.productSpec,
            illustrationRequest.loanRateType
          ),
          inforceProductType === ProductType.WholeLife
            ? this.basicIllustrationData.wholeLifeInforceIllustration.payload.policy.issuedDate
            : null,
          this.basicIllustrationAdditionalData.productBlock
        ),
        footer: allButLastPageFooter,
      },
    ];

    let pageInfos: PageInfo[] = [
      {
        offset: 1,
        footer: allButLastPageFooter,
        title: 'Narrative Summary',
      },
      {
        offset: 2,
        footer: allButLastPageFooter,
        title: 'Narrative Summary',
      },
      {
        offset: 3,
        footer: allButLastPageFooter,
        title: 'Narrative Summary',
      },
    ];
    let tabularPages: number;
    let supplementalPages: number = 0;
    // TO-DO Switch out Illustration summary or to replace with what is needed?
    if (inforceProductType === ProductType.WholeLife) {
      tabularPages = IllustrationSummary.getPagesFromRows(
        this.basicIllustrationData.wholeLifeInforceIllustration.payload.policy.illustrationList.length,
        constants.illustrationRowsOnFirstPage,
        constants.illustrationRowsOnOtherPages
      );
      if (hasSupplemental) {
        supplementalPages = IllustrationSummary.getPagesFromRows(
          this.supplementalIllustrationData.wholeLifeInforceIllustration.payload.policy.illustrationList.length,
          constants.illustrationRowsOnFirstPage,
          constants.illustrationRowsOnOtherPages
        );
      }
    } else if (inforceProductType === ProductType.Term) {
      tabularPages = IllustrationSummary.getPagesFromRows(
        this.basicIllustrationData.termInforceIllustration.payload.policies[0].policyYear.length,
        constants.illustrationRowsOnFirstPage,
        constants.illustrationRowsOnOtherPages
      );
    }
    const totalPages: number = pagesBeforeTabular + tabularPages + supplementalPages; // Coverpage and narrative summary plus tabular pages

    // Add as many tabular values pages as needed, and add corresponding page info
    ({ maindocument, pageInfos } = await this.buildTablePages(
      tabularPages,
      tabularPages,
      maindocument,
      illustrationRequest,
      inforceProductType,
      wholeLifePaymentOptions,
      allButLastPageFooter,
      lastPageFooter,
      pagesBeforeTabular,
      totalPages,
      pageInfos,
      false
    ));
    ({ maindocument, pageInfos } = await this.buildTablePages(
      supplementalPages,
      tabularPages,
      maindocument,
      illustrationRequest,
      inforceProductType,
      supplementalWholeLifePaymentOptions,
      allButLastPageFooter,
      lastPageFooter,
      pagesBeforeTabular,
      totalPages,
      pageInfos,
      true
    ));

    const conversionPageOffset = !!illustrationRequest.isPartialConversion ? 1 : 0;
    if (inforceProductType === ProductType.Term) {
      if (!!illustrationRequest.isPartialConversion) {
        maindocument = maindocument.concat({
          html: await this.productWarning.getHtmlContent(illustrationRequest),
          footer: allButLastPageFooter,
        });

        pageInfos = pageInfos.concat({
          offset: totalPages,
          footer: allButLastPageFooter,
        });
      }
    }

    const pagesToExclude = [];
    if (!!illustrationRequest.isPartialConversion) {
      pagesToExclude.push(totalPages);
    }
    this.addHeadersAndFootersToEachPage(maindocument, pageInfos, header, pagesToExclude);

    return [...[coverpage], ...maindocument].map((section, i) => {
      // appends common styles exactly once to each section
      section.html = section.html.replace(
        `</style>`,
        `${BasicIllustrationStyles.general}${BasicIllustrationStyles.fonts}${Footer.getFooterStyle()}</style>`
      );
      section.html = section.html.replace(/{{pages}}/g, `${totalPages + conversionPageOffset}`);

      // add PDFStream info
      section.formOrder = `${i}`;
      section.formType = 'htmlFile';
      return section;
    });
  }

  async buildTablePages(
    pages: number,
    tabularPages: number,
    maindocument: Body[],
    illustrationRequest: IllustrationRequest,
    inforceProductType: string,
    paymentOptions: WholeLifePaymentTerms,
    allButLastPageFooter: string,
    lastPageFooter: string,
    pagesBeforeTabular: number,
    totalPages: number,
    pageInfos: PageInfo[],
    isSupplemental: boolean
  ) {
    // // Add as many tabular values pages as needed, and add corresponding page info
    for (const i of _.range(pages).map((i) => i + 1)) {
      maindocument = maindocument.concat({
        html: await this.getHtmlContent_InforceIllustrationTabularData(
          illustrationRequest,
          i,
          inforceProductType,
          paymentOptions,
          isSupplemental
        ),
        footer: allButLastPageFooter,
      } as Body);

      // this must be called before getFooter is called for lowerdividend options to show
      IllustrationBasedOnDiv.createLowerDividendFooterMap(illustrationRequest.dividendInterestAdjustment);
      // If whole life inforce, use supplemental footer text
      // Otherwise, use basic disclaimer text
      const incrementalPageInfo: PageInfo = {
        offset: pagesBeforeTabular + (isSupplemental ? tabularPages : 0) - 1 + i,
        disclaimer:
          inforceProductType === ProductType.WholeLife
            ? IllustrationBasedOnDiv.getFooter(
                `Tabular${illustrationRequest.dividendOption}`,
                illustrationRequest,
                true
              )
            : `<div class="footer-text">
                  <p class="p-left">Refer to the Narrative Summary for assumptions, explanations and additional information.</p>
                  <p class="p-left">*The Total Annual Premium in Year ${
                    Utils.getActualAge(illustrationRequest.startDate) + 2
                  } reflects remaining premium payments on this policy for the current year.</p>
                </div>`,
      };

      const isLastPage = totalPages - incrementalPageInfo.offset <= 1;

      if (isLastPage) {
        incrementalPageInfo.footer = lastPageFooter;
      } else {
        incrementalPageInfo.footer = allButLastPageFooter;
      }

      pageInfos = pageInfos.concat(incrementalPageInfo);
    }

    return { maindocument, pageInfos };
  }
  /**
   *  This gets the body of the PDF and where most of the work happens calling other parts of the PDF
   * @param maindocument:  Body[], @param pageInfos: PageInfo[], @param header
   */
  private addHeadersAndFootersToEachPage(
    maindocument: Body[],
    pageInfos: PageInfo[],
    header: string,
    pagesToExclude?: number[]
  ) {
    _.zipWith(maindocument, pageInfos, (section, pageInfo) => {
      // append header and footer styles and bodies into the html documents of the sections (but not the coverpage)
      section.html = section.html.replace(`</style>`, `${Header.getHeaderStyle()}</style>`);
      if ((!!pagesToExclude && pagesToExclude.includes(pageInfo.offset)) === false) {
        section.html = section.html.replace(`</body>`, `${header}${pageInfo.footer}</body>`);
      } else {
        section.html = section.html.replace('</body>', `${pageInfo.footer}</body>`);
      }

      const pageOptions = {
        header: {
          height: constants.HeaderHeight.Normal,
        },
        footer: {
          height: constants.FooterHeight.Normal,
          paginationOffset: pageInfo.offset,
        },
      };

      // add title and/or disclaimer as specifid in pageInfos, and adjust heights accordingly
      if (pageInfo.title) {
        section.html = section.html.replace(
          `<hr class="headerbreak" />`,
          `<hr class="headerbreak" /><h1 class="p-center"> ${pageInfo.title}</h1>`
        );
        pageOptions.header.height = constants.HeaderHeight.Title;
      }
      if (pageInfo.disclaimer) {
        section.html = section.html.replace(
          `<hr class="footerbreak" />`,
          `${pageInfo.disclaimer}<hr class="footerbreak" />`
        );
        pageOptions.footer.height = constants.FooterHeight.Disclaimer;
      }

      (section as Body).options = { ...(section as Body).options, ...pageOptions };

      return section;
    });
  }
  /**
   *  Gets the headers for the http request
   *
   */
  getHeaders(): Header {
    const header = {
      authorization: 'Bearer ' + Utils.getAuthTokenForPdfstream(),
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };
    return header;
  }

  /**
   *  Gets the URLs for the download
   *
   */
  getHtmlToPdfStreamUrlForAgentChannel(): string {
    return `${this.amorphic.config.nconf.get('pdfstreamUrl')}/fill/Agent`;
  }

  async getHtmlContent_InforceIllustrationTabularData(
    illustrationRequest: IllustrationRequest,
    pageNumber: number,
    inforceProductType: string,
    wholeLifePaymentOptions?: WholeLifePaymentTerms,
    isSupplemental: boolean = false
  ) {
    const rowsOnPage1 = constants.illustrationRowsOnFirstPage;
    const rowsOnOtherPages = constants.illustrationRowsOnOtherPages;
    const style: string = `${IllustrationSummary.getStyles()}`;
    let body: string = '';

    const illustrationData = isSupplemental ? this.supplementalIllustrationData : this.basicIllustrationData;
    const additionalIllustrationData = isSupplemental
      ? this.supplementalIllustrationAdditionalData
      : this.basicIllustrationAdditionalData;
    const paymentOptions = additionalIllustrationData.paymentOptions;
    const hasWaiver = illustrationRequest.waiverUnits > 0;
    let tableLabel = 'Tabular';
    let currentColumns = this.tableColumns;
    let columnHeaderText = null;
    if (isSupplemental) {
      tableLabel = 'Supplemental';
      currentColumns = this.supplementalTableColumns;
      columnHeaderText = HtmlPdfUtils.buildColumnHeaderText(currentColumns, isSupplemental);
    }

    if (pageNumber === 1) {
      const startIndex = 0;
      body += `${
        inforceProductType === ProductType.WholeLife
          ? await this.summaryTable.getHtmlContent_SummaryTable_WholeLife(
              illustrationRequest,
              wholeLifePaymentOptions,
              isSupplemental
            )
          : await this.summaryTable.getHtmlContent_SummaryTable(illustrationRequest, paymentOptions)
      }`;
      body += `<h1 class="p-center">${tableLabel} Values</h1>`;
      body +=
        inforceProductType === ProductType.WholeLife
          ? `${IllustrationBasedOnDiv.getIllustrationTableBasedOnDiv(
              illustrationRequest,
              startIndex,
              rowsOnPage1,
              illustrationData,
              true,
              isSupplemental,
              currentColumns,
              columnHeaderText
            )}`
          : `${IllustrationBasedOnDiv.getInforceIllustrationTableBasedOnDiv(
              startIndex,
              rowsOnPage1,
              illustrationData,
              hasWaiver,
              additionalIllustrationData.productBlock
            )}`;
    } else {
      const startIndex = pageNumber > 1 ? rowsOnPage1 + rowsOnOtherPages * (pageNumber - 2) : 0;
      body += `<h1 class="p-center">${tableLabel} Values</h1>`;
      body +=
        inforceProductType === ProductType.WholeLife
          ? `${IllustrationBasedOnDiv.getIllustrationTableBasedOnDiv(
              illustrationRequest,
              startIndex,
              rowsOnOtherPages,
              illustrationData,
              true,
              isSupplemental,
              currentColumns,
              columnHeaderText
            )}`
          : `${IllustrationBasedOnDiv.getInforceIllustrationTableBasedOnDiv(
              startIndex,
              rowsOnOtherPages,
              illustrationData,
              hasWaiver,
              additionalIllustrationData.productBlock
            )}`;
    }

    return HtmlPdfUtils.getHtmlContent(style, body);
  }

  //~~~~~~~~~~~ Almost everything below this will probably be moved to another file like Utils or its own function this is just here to generate 'pages' ~~~~//
  getHtmlContent_IllustrationTabularData(illustrationRequest: IllustrationRequest, pageNumber: number): string {
    //TODO : HL-6890 : Once we have real data the number of rows on a page and total number of pages will be calculated here
    const rowsOnPage1 = constants.illustrationRowsOnFirstPage;
    const rowsOnOtherPages = constants.illustrationRowsOnOtherPages;

    const paymentOptions = this.basicIllustrationAdditionalData.paymentOptions;
    let body: string = '';

    if (pageNumber === 1) {
      const startIndex = 0;
      body += `${IllustrationSummary.getSummary(illustrationRequest)}`;
      body += `${IllustrationBasedOnDiv.getTableTitle()}`;
      body += `${IllustrationBasedOnDiv.getIllustrationTableBasedOnDiv(
        illustrationRequest,
        startIndex,
        rowsOnPage1,
        this.basicIllustrationData
      )}`;
    } else {
      const startIndex = pageNumber > 1 ? rowsOnPage1 + rowsOnOtherPages * (pageNumber - 2) : 0;
      body += `${IllustrationBasedOnDiv.getIllustrationTableBasedOnDiv(
        illustrationRequest,
        startIndex,
        rowsOnOtherPages,
        this.basicIllustrationData
      )}`;
    }
    const style: string = `${IllustrationSummary.getStyles()}`;
    let hasAlir = false;

    if (paymentOptions.annual) {
      body = body
        .replace(/{{FaceAmount}}/g, Utils.formatNumber(illustrationRequest.faceAmount))
        .replace(/{{BasePremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.basePremium, 2), 2))
        .replace(/{{BasePremiumMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.basePremium, 2), 2))
        .replace(/{{WaiverAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.waiver, 2), 2))
        .replace(/{{WaiverMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.waiver, 2), 2))
        .replace(/{{FlatExtraAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.flatExtra, 2), 2))
        .replace(/{{FlatExtraMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.flatExtra, 2), 2))
        .replace(/{{ModalPremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.total, 2), 2))
        .replace(/{{ModalPremiumMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.total, 2), 2))
        .replace(/{{ModalAnnualizedPremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.total, 2), 2))
        .replace(
          /{{BasePremiumSemiAnnual}}/,
          Utils.formatNumber(Utils.round(paymentOptions.semiannual.basePremium, 2), 2)
        )
        .replace(
          /{{BasePremiumQuarterly}}/,
          Utils.formatNumber(Utils.round(paymentOptions.quarterly.basePremium, 2), 2)
        )
        .replace(/{{ModalPremiumSemiAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.semiannual.total, 2), 2))
        .replace(/{{ModalPremiumQuarterly}}/, Utils.formatNumber(Utils.round(paymentOptions.quarterly.total, 2), 2))
        .replace(
          /{{ModalAnnualizedPremiumSemiAnnual}}/,
          Utils.formatNumber(Utils.round(paymentOptions.semiannual.annualTotal, 2), 2)
        )
        .replace(
          /{{ModalAnnualizedPremiumQuarterly}}/,
          Utils.formatNumber(Utils.round(paymentOptions.quarterly.annualTotal, 2), 2)
        )
        .replace(
          /{{ModalAnnualizedPremiumMonthly}}/,
          Utils.formatNumber(Utils.round(paymentOptions.monthly.annualTotal, 2), 2)
        );
    }

    if (illustrationRequest.riders) {
      illustrationRequest.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body.replace(/{{GirFaceAmount}}/g, Utils.formatNumber(Utils.round(rider.amount, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body.replace(/{{RtrFaceAmount}}/g, Utils.formatNumber(Utils.round(rider.amount, 2), 2));
        }
        if (rider.type === RiderType.ALIR) {
          hasAlir = true;
          body = body.replace(
            /{{AlirFaceAmount}}/g,
            Utils.formatNumber(
              Utils.round(this.basicIllustrationData.illustration.payload.policy.alirAtIssueUnits, 2),
              2
            )
          );
        }
      });
    }

    if (paymentOptions.annual.riders) {
      paymentOptions.annual.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body
            .replace(/{{GirAnnual}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{GirWaiverAnnual}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body
            .replace(/{{RtrAnnual}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{RtrWaiverAnnual}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
      });
      if (hasAlir) {
        const alirRider = illustrationRequest.riders.find((rider) => rider.type === RiderType.ALIR);
        body = body.replace(/{{AlirAnnual}}/, Utils.formatNumber(Utils.round(alirRider.amount, 2), 2));
        body = body.replace(/{{AlirMonthly}}/, Utils.formatNumber(Utils.round(alirRider.amount / 12, 2), 2));
      }
    }

    if (paymentOptions.annual.riders) {
      paymentOptions.monthly.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body
            .replace(/{{GirMonthly}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{GirWaiverMonthly}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body
            .replace(/{{RtrMonthly}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{RtrWaiverMonthly}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
      });
    }

    return HtmlPdfUtils.getHtmlContent(style, body);
  }

  paymentOption;

  getPaymentOptions(premiumData) {
    // In the Illustration service request, the payment frequency of the first policy to be monthly and annual to the second one.
    const [monthly, annual] = premiumData;
    const paymentOptions = {
      annual: this.getPaymentOptionValuesFromSegments(annual.segments, ProductApiTypes.PaymentFrequency.Annual),
      monthly: this.getPaymentOptionValuesFromSegments(monthly.segments, ProductApiTypes.PaymentFrequency.Monthly),
    };

    paymentOptions.monthly.annualTotal = paymentOptions.monthly.total * 12;
    paymentOptions.monthly.additionalCharge = paymentOptions.monthly.annualTotal - paymentOptions.annual.total;

    return paymentOptions;
  }

  getPaymentOptionValuesFromSegments(segments, paymentFrequency) {
    let paymentOption: PaymentOption;

    if (paymentFrequency === ProductApiTypes.PaymentFrequency.Annual) {
      paymentOption = {
        basePremium: 0,
        premium: 0,
        waiver: 0,
        flatExtra: 0,
        total: 0,
        riders: [],
      };
    } else if (paymentFrequency === ProductApiTypes.PaymentFrequency.Monthly) {
      paymentOption = {
        basePremium: 0,
        premium: 0,
        waiver: 0,
        flatExtra: 0,
        total: 0,
        annualPremium: 0,
        annualWaiver: 0,
        annualTotal: 0,
        additionalCharge: 0,
        riders: [],
      };
    } else {
      const error = {
        isHumanRelated: false,
        data: {
          paymentFrequency: paymentFrequency,
        },
      };
      //throw error?
      UILoggerSingleton.getInstance().error(
        {
          module: 'HtmlPdfController',
          function: 'getPaymentOptionValuesFromSegments',
          error: error,
          category: UICategory.request,
        },
        'Unsupported payment frequency'
      );
    }
    segments.forEach((segment) => {
      const riderType: string = segment.riderType;
      const benefits = segment.benefits;
      const rider: IllustrationRider = {
        type: riderType,
        premium: 0,
        waiver: 0,
      };

      benefits.forEach((benefit) => {
        switch (benefit.code) {
          case 'Death':
            if (riderType === RiderType.RTR) {
              //death benefit for RTR segment
              rider.premium = benefit.basePremium;
            } else {
              //death benefit for base policy segment

              paymentOption.basePremium =
                paymentFrequency === ProductApiTypes.PaymentFrequency.Annual
                  ? this.calculateBasePremium(benefit, ProductApiTypes.PaymentFrequency.Annual)
                  : this.calculateBasePremium(benefit, ProductApiTypes.PaymentFrequency.Monthly);
              if (paymentFrequency === ProductApiTypes.PaymentFrequency.Monthly) {
                //set annual premium for montly payment option
                paymentOption.annualPremium = paymentOption.premium * 12;
              }
            }

            paymentOption.premium = benefit.premium;
            //update total and flat extras for all segment types
            paymentOption.total += paymentOption.premium;
            benefit.flatExtras.forEach((flatExtra) => {
              paymentOption.flatExtra += flatExtra.premium;
            });
            break;
          case RiderType.GIR: {
            //GIR benefit for GIR segment
            // update annual.premium? flat extras?
            paymentOption.total += benefit.premium;
            rider.premium = benefit.basePremium;
            break;
          }
          case 'Waiver':
            if (riderType === RiderType.GIR || riderType === RiderType.RTR) {
              //waiver benefit for GIR or RTR segment
              rider.waiver = benefit.premium;
            } else {
              //waiver benefit for base policy segment
              paymentOption.waiver = benefit.premium;
              if (paymentFrequency === ProductApiTypes.PaymentFrequency.Monthly) {
                //set annual waiver only for monthly payment option
                paymentOption.annualWaiver = paymentOption.waiver * 12;
              }
            }
            //update total to include waiver benefit for all segment types
            paymentOption.total += benefit.premium;

            break;
        }
      });
      if (riderType) {
        //if current segment is a rider, add rider to array
        paymentOption.riders.push(rider);
      }
    });

    return paymentOption;
  }

  calculateBasePremium(benefit, paymentFrequency: ProductApiTypes.PaymentFrequency): number {
    const baseKey = paymentFrequency === ProductApiTypes.PaymentFrequency.Annual ? 'annualBasePremium' : 'basePremium';
    const feeKey = paymentFrequency === ProductApiTypes.PaymentFrequency.Annual ? 'annualFee' : 'fee';
    const tableRatingPremiumKey =
      paymentFrequency === ProductApiTypes.PaymentFrequency.Annual ? 'annualTableRatingPremium' : 'tableRatingPremium';

    return benefit[baseKey] + benefit[feeKey] + benefit[tableRatingPremiumKey];
  }

  getLoanInterestAmountFromSpec(productSpec: ProductSpecResponse | null, loanType: string): number {
    if (!productSpec) {
      // For Term policies
      return null;
    }
    if (loanType === LoanType.VARIABLE) {
      return productSpec.variableLoanInterestRate;
    } else {
      return productSpec.fixedLoanInterestRate;
    }
  }
}
